import { defineStore } from 'pinia'
import { useApiConfig } from '@/config/api'
import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  setAuthSession
} from '~/utils/authToken'
import { sessionExpired } from '~/utils/sessionExpired'
import { readPrinting, type Printing } from '~/utils/printRelay'

export const ADMIN_ROLE_NAME = 'Admin'

export type PermissionCatalogEntry = {
  key: string
  description: string
}

type AuthUserPayload = {
  id?: string | null
  userId?: string | null
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  displayName?: string | null
  company?: string | null
  roles?: string[] | null
  permissions?: string[] | null
  resources?: string[] | null
  effectiveActorName?: string | null
  mustChangePassword?: boolean | null
  isPartnerUser?: boolean | null
  partnerIds?: number[] | null
  isGeneric?: boolean | null
  printing?: string | null
}

export type AuthUser = {
  id: string
  userId: string
  username: string
  firstName: string
  lastName: string
  fullName: string
  displayName: string
  company: string
  roles: string[]
  permissions: string[]
  /// The session's Effective Actor — "First Last (employee_number)" when a generic
  /// shared account completed employee verification, '' otherwise (ID15). The header
  /// prefers this so the UI names the same person the record stamps do.
  effectiveActorName: string
  /** The password was set by someone else: the user sets their own before anything else (task 2459). */
  mustChangePassword: boolean
  /**
   * A partner's own person (task 1309): they see only their partners' data and only the partner
   * screens (navigation/nav.ts, PARTNER_PATHS). The server enforces both; the screens follow it.
   */
  isPartnerUser: boolean
  partnerIds: number[]
  /** A shared tablet log-in (the Shared Login tick): it goes by its role on screen (utils/userNames.ts). */
  isGeneric: boolean
  /** Where they may print (task 3490): the site's printers, this device, or both (utils/printRelay.ts). */
  printing: Printing
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const normalized = value
    .map(item => readString(item))
    .filter(Boolean)

  return Array.from(new Set(normalized))
}

function maskIdentifier(value: string): string {
  const raw = readString(value)
  if (!raw) return ''
  if (raw.length <= 2) return `${raw[0]}*`
  return `${raw.slice(0, 2)}***`
}

function isNetworkFetchError(error: unknown): boolean {
  const name = String((error as { name?: unknown })?.name ?? '').toLowerCase()
  const message = String((error as { message?: unknown })?.message ?? '').toLowerCase()
  return name === 'typeerror' && (
    message.includes('failed to fetch')
    || message.includes('networkerror')
    || message.includes('load failed')
  )
}

function getClientAuthContext() {
  if (!import.meta.client) return { runtime: 'server' }

  return {
    runtime: 'client',
    href: globalThis.location?.href ?? '',
    origin: globalThis.location?.origin ?? '',
    host: globalThis.location?.host ?? '',
    userAgent: globalThis.navigator?.userAgent ?? ''
  }
}

function normalizeUserPayload(payload: AuthUserPayload | null | undefined): AuthUser {
  const id = readString(payload?.id ?? '')
  const userId = readString(payload?.userId ?? id)
  const username = readString(payload?.username ?? '')
  const firstName = readString(payload?.firstName ?? '')
  const lastName = readString(payload?.lastName ?? '')
  const fullName = readString(payload?.fullName ?? `${firstName} ${lastName}`.trim())
  const displayName = readString((payload?.displayName ?? fullName) || username)
  const company = readString(payload?.company ?? '')
  const roles = readStringArray(payload?.roles)
  const permissions = readStringArray(payload?.permissions ?? payload?.resources)
  const effectiveActorName = readString(payload?.effectiveActorName ?? '')

  return {
    id,
    userId: userId || id,
    username,
    firstName,
    lastName,
    fullName,
    displayName,
    company,
    roles,
    permissions,
    effectiveActorName,
    mustChangePassword: payload?.mustChangePassword === true,
    isPartnerUser: payload?.isPartnerUser === true,
    partnerIds: Array.isArray(payload?.partnerIds) ? payload.partnerIds.filter((x): x is number => typeof x === 'number') : [],
    isGeneric: payload?.isGeneric === true,
    printing: readPrinting(payload?.printing),
  }
}

/**
 * What the log-in screen says when the server did not answer: the request never
 * landed, took longer than ten seconds, or the server could not respond. The
 * same words as the tablet's log-in (Mark, 2026-10-15), because the fix is the
 * person's own -- and "unable to log in" reads as a wrong password.
 */
export const CANNOT_REACH_SERVER = 'Cannot reach the server. Check the connection and try again.'
const AUTH_REACH_TIMEOUT_MS = 10_000

class ServerUnreachableError extends Error {}

/** `fetch` for the log-in steps, with the tablet's ten-second limit and its "no answer" statuses. */
async function reachAuth(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController()
  const timer = globalThis.setTimeout(() => controller.abort(), AUTH_REACH_TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(url, { ...init, signal: controller.signal })
  } catch {
    throw new ServerUnreachableError('No answer from the server')
  } finally {
    globalThis.clearTimeout(timer)
  }
  if (res.status >= 500 || res.status === 408 || res.status === 429) {
    throw new ServerUnreachableError(`The server could not answer (${res.status})`)
  }
  return res
}

function cannotReach(error: unknown): string | null {
  return error instanceof ServerUnreachableError ? CANNOT_REACH_SERVER : null
}

const DEV_PERMISSIONS_LOG_KEY = '__dockautomation_permissions_logged__'
const AUTH_ME_TIMEOUT_MS = 5000

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    user: null as AuthUser | null,
    roles: [] as string[],
    permissions: new Set<string>(),
    isLoading: false,
    isLoaded: false,
    accessToken: null as string | null,
    refreshToken: null as string | null,
    pendingEmployeeChallengeToken: null as string | null,
    // The generic account's roster, shown read-only in the "Who's working" step —
    // the worker never picks a roster at login (ID14 slice 8).
    pendingEmployeeRosterName: null as string | null,
    loadMePromise: null as Promise<boolean> | null,
    permissionCatalog: [] as PermissionCatalogEntry[],
    isLoadingCatalog: false,
    catalogPromise: null as Promise<PermissionCatalogEntry[]> | null
  }),

  getters: {
    isAuthenticated: state => Boolean(state.user && state.accessToken),
    isAdmin: state => state.roles.includes(ADMIN_ROLE_NAME),
    /** A partner's own person (task 1309): only the partner screens, only their data. */
    isPartnerUser: state => state.user?.isPartnerUser === true
  },

  actions: {
    async loadMe(force = false) {
      if (!force && this.isLoaded) {
        return this.isAuthenticated
      }

      if (!force && this.loadMePromise) {
        return await this.loadMePromise
      }

      const pendingLoad = this.loadMeInternal(force)
      this.loadMePromise = pendingLoad

      try {
        return await pendingLoad
      } finally {
        this.loadMePromise = null
      }
    },

    async loadMeInternal(force = false) {
      if (force) {
        this.isLoaded = false
      }

      this.accessToken = getAccessToken()
      this.refreshToken = getRefreshToken()

      if (!this.accessToken) {
        this.clearIdentityState()
        this.isLoaded = true
        return false
      }

      return await this.fetchMe()
    },

    async login(credentials: { username: string, password: string }) {
      try {
        this.isLoading = true
        const apiConfig = useApiConfig()
        const endpoint = `${apiConfig.authURL}/login`
        const clientContext = getClientAuthContext()

        const res = await reachAuth(endpoint, {
          method: 'POST',
          headers: apiConfig.headers,
          body: JSON.stringify({
            username: credentials.username,
            password: credentials.password
          })
        })

        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          console.error('[auth] Login request failed', {
            endpoint,
            status: res.status,
            statusText: res.statusText,
            username: maskIdentifier(credentials.username),
            serverMessage,
            ...clientContext
          })
          this.clearPendingEmployeeVerification()
          return {
            success: false,
            requiresEmployeeVerification: false,
            message: serverMessage || 'Invalid username or password'
          }
        }

        const payload = await res.json()
        const requiresEmployeeVerification = Boolean(payload?.requiresEmployeeVerification)

        if (requiresEmployeeVerification) {
          const challengeToken = readString(payload?.employeeChallengeToken)
          if (!challengeToken) {
            console.error('[auth] Login requested employee verification but challenge token missing', {
              endpoint,
              username: maskIdentifier(credentials.username),
              ...clientContext
            })
            this.clearPendingEmployeeVerification()
            return {
              success: false,
              requiresEmployeeVerification: false,
              message: 'Employee verification token was not provided by the server.'
            }
          }

          this.clearAuthSessionState()
          this.pendingEmployeeChallengeToken = challengeToken
          this.pendingEmployeeRosterName = readString(payload?.employeeRosterName)
          this.user = payload?.user ? normalizeUserPayload(payload.user as AuthUserPayload) : null
          this.roles = this.user?.roles ?? []
          this.permissions = new Set(this.user?.permissions ?? [])
          this.isLoaded = true

          return {
            success: false,
            requiresEmployeeVerification: true,
            message: null
          }
        }

        const accessToken = readString(payload?.accessToken)
        const refreshToken = readString(payload?.refreshToken)

        if (!accessToken || !refreshToken) {
          console.error('[auth] Login response missing tokens', {
            endpoint,
            username: maskIdentifier(credentials.username),
            hasAccessToken: Boolean(accessToken),
            hasRefreshToken: Boolean(refreshToken),
            ...clientContext
          })
          this.clearPendingEmployeeVerification()
          return {
            success: false,
            requiresEmployeeVerification: false,
            message: 'Login response did not include valid access tokens.'
          }
        }

        setAuthSession({
          accessToken,
          refreshToken,
          accessTokenExpiresAtUtc: payload?.accessTokenExpiresAtUtc ?? null,
          refreshTokenExpiresAtUtc: payload?.refreshTokenExpiresAtUtc ?? null
        })

        this.accessToken = accessToken
        this.refreshToken = refreshToken
        this.clearPendingEmployeeVerification()

        if (payload?.user) {
          this.applyUser(payload.user as AuthUserPayload)
          this.isLoaded = true
          this.logPermissionsInDev()

          return {
            success: true,
            requiresEmployeeVerification: false,
            message: null
          }
        }

        const fetched = await this.loadMe(true)
        if (!fetched) {
          console.error('[auth] Login succeeded but failed to load user profile', {
            endpoint: `${apiConfig.authURL}/me`,
            username: maskIdentifier(credentials.username),
            ...clientContext
          })
        }
        return {
          success: fetched,
          requiresEmployeeVerification: false,
          message: fetched ? null : 'Unable to load user profile.'
        }
      } catch (error) {
        console.error('[auth] Login exception', {
          error,
          username: maskIdentifier(credentials.username),
          ...getClientAuthContext()
        })
        this.clearPendingEmployeeVerification()
        return {
          success: false,
          requiresEmployeeVerification: false,
          message: cannotReach(error) ?? 'Unable to log in right now.'
        }
      } finally {
        this.isLoading = false
      }
    },

    /// Resolve the employee number WITHIN the account's roster and return the
    /// person's name WITHOUT issuing tokens — the worker confirms the name first,
    /// then verifyEmployee starts the session (ID14 slice 8).
    async resolveEmployee(employeeNumber: string) {
      try {
        this.isLoading = true
        const apiConfig = useApiConfig()
        const challengeToken = readString(this.pendingEmployeeChallengeToken)
        if (!challengeToken) {
          return { success: false, message: 'Employee verification session has expired. Log in again.' }
        }

        const res = await reachAuth(`${apiConfig.authURL}/resolve-employee`, {
          method: 'POST',
          headers: apiConfig.headers,
          body: JSON.stringify({ employeeChallengeToken: challengeToken, employeeNumber })
        })

        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          return { success: false, message: serverMessage || 'Employee number is not active.' }
        }

        const payload = await res.json()
        return {
          success: true,
          fullName: readString(payload?.fullName) ?? employeeNumber,
          employeeRosterName: readString(payload?.employeeRosterName),
          message: null
        }
      } catch (error) {
        console.error('[auth] Employee resolve exception', { error })
        return { success: false, message: cannotReach(error) ?? 'Unable to look up that employee number.' }
      } finally {
        this.isLoading = false
      }
    },

    async verifyEmployee(employeeNumber: string) {
      try {
        this.isLoading = true
        const apiConfig = useApiConfig()
        const endpoint = `${apiConfig.authURL}/verify-employee`
        const clientContext = getClientAuthContext()

        const challengeToken = readString(this.pendingEmployeeChallengeToken)
        if (!challengeToken) {
          console.error('[auth] Employee verification attempted without active challenge token', {
            endpoint,
            employeeNumber: maskIdentifier(employeeNumber),
            ...clientContext
          })
          return {
            success: false,
            message: 'Employee verification session has expired. Log in again.'
          }
        }

        const res = await reachAuth(endpoint, {
          method: 'POST',
          headers: apiConfig.headers,
          body: JSON.stringify({
            employeeChallengeToken: challengeToken,
            employeeNumber
          })
        })

        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          console.error('[auth] Employee verification request failed', {
            endpoint,
            status: res.status,
            statusText: res.statusText,
            employeeNumber: maskIdentifier(employeeNumber),
            serverMessage,
            ...clientContext
          })
          return {
            success: false,
            message: serverMessage || 'Employee verification failed.'
          }
        }

        const payload = await res.json()
        const accessToken = readString(payload?.accessToken)
        const refreshToken = readString(payload?.refreshToken)

        if (!accessToken || !refreshToken) {
          console.error('[auth] Employee verification response missing tokens', {
            endpoint,
            employeeNumber: maskIdentifier(employeeNumber),
            hasAccessToken: Boolean(accessToken),
            hasRefreshToken: Boolean(refreshToken),
            ...clientContext
          })
          return {
            success: false,
            message: 'Verification response did not include valid access tokens.'
          }
        }

        setAuthSession({
          accessToken,
          refreshToken,
          accessTokenExpiresAtUtc: payload?.accessTokenExpiresAtUtc ?? null,
          refreshTokenExpiresAtUtc: payload?.refreshTokenExpiresAtUtc ?? null
        })

        this.accessToken = accessToken
        this.refreshToken = refreshToken
        this.clearPendingEmployeeVerification()

        // Apply the embedded user first so the session works even if /me hiccups…
        const hasEmbeddedUser = Boolean(payload?.user)
        if (payload?.user) {
          this.applyUser(payload.user as AuthUserPayload)
          this.isLoaded = true
          this.logPermissionsInDev()
        }

        // …then re-fetch /me under the NEW token regardless: only /me carries
        // effectiveActorName (ID15), and the verify response's embedded user
        // doesn't — without this the header keeps showing the shared account
        // instead of the person who just verified.
        const fetched = await this.loadMe(true)
        if (!fetched) {
          console.error('[auth] Employee verification succeeded but failed to load user profile', {
            endpoint: `${apiConfig.authURL}/me`,
            employeeNumber: maskIdentifier(employeeNumber),
            ...clientContext
          })
        }
        const success = hasEmbeddedUser || fetched
        return {
          success,
          message: success ? null : 'Unable to load user profile.'
        }
      } catch (error) {
        console.error('[auth] Employee verification exception', {
          error,
          employeeNumber: maskIdentifier(employeeNumber),
          ...getClientAuthContext()
        })
        return {
          success: false,
          message: cannotReach(error) ?? 'Unable to verify employee right now.'
        }
      } finally {
        this.isLoading = false
      }
    },

    async requestPasswordReset(email: string) {
      const apiConfig = useApiConfig()
      const trimmed = readString(email)
      if (!trimmed) {
        return { success: false, message: 'Email is required.' }
      }

      try {
        const res = await fetch(`${apiConfig.authURL}/password-reset/request`, {
          method: 'POST',
          headers: apiConfig.headers,
          body: JSON.stringify({ email: trimmed })
        })

        // Spec: this endpoint always returns 200 to avoid leaking valid emails.
        // Treat any 2xx as success and surface a generic confirmation upstream.
        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          return {
            success: false,
            message: serverMessage || 'Unable to send reset email right now.'
          }
        }
        return { success: true, message: null as string | null }
      } catch (error) {
        console.error('[auth] Password reset request failed', { error })
        return { success: false, message: 'Unable to send reset email right now.' }
      }
    },

    async confirmPasswordReset(token: string, newPassword: string) {
      return await this.postPasswordWithToken('password-reset/confirm', token, newPassword, 'reset')
    },

    async acceptInvite(token: string, newPassword: string) {
      return await this.postPasswordWithToken('invite/accept', token, newPassword, 'invite')
    },

    async postPasswordWithToken(path: string, token: string, newPassword: string, kind: 'reset' | 'invite') {
      const apiConfig = useApiConfig()
      const trimmedToken = readString(token)
      const trimmedPassword = readString(newPassword)

      if (!trimmedToken) {
        return { success: false, message: 'This link is missing its token. Request a new one.' }
      }
      if (!trimmedPassword) {
        return { success: false, message: 'A new password is required.' }
      }

      try {
        const res = await fetch(`${apiConfig.authURL}/${path}`, {
          method: 'POST',
          headers: apiConfig.headers,
          body: JSON.stringify({ token: trimmedToken, newPassword: trimmedPassword })
        })

        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          const fallback = kind === 'invite'
            ? 'This invite link is invalid or has expired.'
            : 'This reset link is invalid or has expired.'
          return { success: false, message: serverMessage || fallback }
        }
        return { success: true, message: null as string | null }
      } catch (error) {
        console.error(`[auth] Password ${kind} request failed`, { error })
        return { success: false, message: 'Unable to complete the request right now.' }
      }
    },

    async changePassword(currentPassword: string, newPassword: string) {
      const apiConfig = useApiConfig()
      const accessToken = this.accessToken || getAccessToken()
      if (!accessToken) {
        return { success: false, message: 'You must be logged in to change your password.' }
      }

      const trimmedCurrent = readString(currentPassword)
      const trimmedNew = readString(newPassword)
      if (!trimmedCurrent || !trimmedNew) {
        return { success: false, message: 'Both the current and new passwords are required.' }
      }

      try {
        const res = await fetch(`${apiConfig.authURL}/password-change`, {
          method: 'POST',
          headers: {
            ...apiConfig.headers,
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({ currentPassword: trimmedCurrent, newPassword: trimmedNew })
        })

        if (!res.ok) {
          const serverMessage = await this.readErrorMessage(res)
          return {
            success: false,
            message: serverMessage || 'Unable to change your password right now.'
          }
        }
        return { success: true, message: null as string | null }
      } catch (error) {
        console.error('[auth] Password change failed', { error })
        return { success: false, message: 'Unable to change your password right now.' }
      }
    },

    async logout() {
      const apiConfig = useApiConfig()
      try {
        if (this.accessToken) {
          await fetch(`${apiConfig.authURL}/logout`, {
            method: 'POST',
            headers: apiConfig.headers
          })
        }
      } catch (error) {
        console.error('Logout error:', error)
      } finally {
        clearAuthSession()
        this.clearAuthSessionState()
        this.clearPendingEmployeeVerification()
        this.isLoaded = true

        if (import.meta.client) {
          const route = useRoute()
          if (route.path !== '/login') {
            await navigateTo('/login')
          }
        }
      }
    },

    async fetchMe() {
      let timeoutId: ReturnType<typeof globalThis.setTimeout> | null = null
      try {
        const accessToken = this.accessToken || getAccessToken()
        if (!accessToken) {
          this.clearIdentityState()
          this.isLoaded = true
          return false
        }

        const apiConfig = useApiConfig()
        const controller = new AbortController()
        timeoutId = globalThis.setTimeout(() => controller.abort(), AUTH_ME_TIMEOUT_MS)

        const res = await fetch(`${apiConfig.authURL}/me`, {
          method: 'GET',
          headers: {
            ...apiConfig.headers,
            Authorization: `Bearer ${accessToken}`
          },
          signal: controller.signal
        })

        if (res.status === 401) {
          await this.handleUnauthorized()
          return false
        }

        if (res.status === 403) {
          this.clearIdentityState()
          this.isLoaded = true
          return false
        }

        if (!res.ok) {
          this.clearIdentityState()
          this.isLoaded = true
          return false
        }

        const payload = await res.json()
        this.applyUser(payload as AuthUserPayload)
        this.isLoaded = true
        this.logPermissionsInDev()
        return true
      } catch (error) {
        if ((error as { name?: string })?.name === 'AbortError') {
          // Timed-out identity fetch should not block app startup; retry later.
          this.isLoaded = false
          return false
        }

        if (isNetworkFetchError(error)) {
          // Keep current auth session for transient transport failures.
          this.isLoaded = false
          return false
        }

        console.error('Fetch user error:', error)
        this.clearIdentityState()
        this.isLoaded = true
        return false
      } finally {
        if (timeoutId) {
          globalThis.clearTimeout(timeoutId)
        }
      }
    },

    has(permission: string) {
      const key = readString(permission)
      if (!key) return false
      if (this.isAdmin) return true
      return this.permissions.has(key)
    },

    hasAll(permissions: string[]) {
      if (!Array.isArray(permissions) || permissions.length === 0) return true
      if (this.isAdmin) return true
      return permissions.every(p => this.has(p))
    },

    hasAny(permissions: string[]) {
      if (!Array.isArray(permissions) || permissions.length === 0) return true
      if (this.isAdmin) return true
      return permissions.some(p => this.has(p))
    },

    async fetchPermissionCatalog(force = false) {
      if (!force && this.permissionCatalog.length > 0) return this.permissionCatalog
      if (!force && this.catalogPromise) return await this.catalogPromise

      const pending = this.fetchPermissionCatalogInternal()
      this.catalogPromise = pending
      try {
        return await pending
      } finally {
        this.catalogPromise = null
      }
    },

    async fetchPermissionCatalogInternal() {
      this.isLoadingCatalog = true
      try {
        const apiConfig = useApiConfig()
        const accessToken = this.accessToken || getAccessToken()
        if (!accessToken) {
          this.permissionCatalog = []
          return this.permissionCatalog
        }

        const res = await fetch(`${apiConfig.permissionsURL}/catalog`, {
          method: 'GET',
          headers: {
            ...apiConfig.headers,
            Authorization: `Bearer ${accessToken}`
          }
        })
        if (!res.ok) {
          this.permissionCatalog = []
          return this.permissionCatalog
        }

        const payload = await res.json()
        if (Array.isArray(payload)) {
          this.permissionCatalog = payload
            .map((entry: unknown) => {
              const e = entry as Record<string, unknown>
              const key = readString(e?.key)
              const description = readString(e?.description)
              return key ? { key, description } : null
            })
            .filter((entry): entry is PermissionCatalogEntry => entry !== null)
        } else {
          this.permissionCatalog = []
        }
        return this.permissionCatalog
      } catch (error) {
        console.error('[auth] Failed to load permission catalog', error)
        this.permissionCatalog = []
        return this.permissionCatalog
      } finally {
        this.isLoadingCatalog = false
      }
    },

    applyUser(rawUser: AuthUserPayload) {
      // Logged in (again): whatever ran out before is over.
      sessionExpired.value = false
      const normalized = normalizeUserPayload(rawUser)
      this.user = normalized
      this.roles = normalized.roles
      this.permissions = new Set(normalized.permissions)
    },

    clearIdentityState() {
      this.user = null
      this.roles = []
      this.permissions = new Set<string>()
    },

    clearAuthSessionState() {
      this.clearIdentityState()
      this.accessToken = null
      this.refreshToken = null
    },

    clearPendingEmployeeVerification() {
      this.pendingEmployeeChallengeToken = null
      this.pendingEmployeeRosterName = null
    },

    async handleUnauthorized() {
      clearAuthSession()
      this.clearAuthSessionState()
      this.clearPendingEmployeeVerification()
      this.isLoaded = true

      if (import.meta.client) {
        const route = useRoute()
        if (route.path !== '/login') {
          await navigateTo('/login')
        }
      }
    },

    logPermissionsInDev() {
      if (!import.meta.dev || !import.meta.client) return

      const globalDev = globalThis as typeof globalThis & Record<string, unknown>
      if (globalDev[DEV_PERMISSIONS_LOG_KEY]) return

      globalDev[DEV_PERMISSIONS_LOG_KEY] = true
      console.info('[auth] Loaded permissions:', Array.from(this.permissions).sort())
    },

    async readErrorMessage(response: Response) {
      try {
        const text = (await response.text()).trim()
        if (!text) return ''

        try {
          const body = JSON.parse(text) as Record<string, unknown>
          const title = readString(body?.title)
          const detail = readString(body?.detail)
          const message = readString(body?.message)

          // Spec: zero-roles login returns ProblemDetails with title="No Roles Assigned".
          if (title === 'No Roles Assigned') {
            return detail || 'Your account has no roles assigned. Contact an administrator.'
          }

          // RFC 9110 priority: detail → title → generic message.
          return detail || title || message
        } catch {
          // Not JSON — fall through to plain text (capped to keep error UI readable).
          return text.length < 500 ? text : ''
        }
      } catch {
        return ''
      }
    }
  }
})
